/**
 * @description
 * Este arquivo cria diferentes instâncias do axios para cada um dos backends utilizados pelo painel.
 * Este arquivo também define os endpoints (rotas) dos diferentes backends utilizados pelo painel.
 *
 * As rotas descritas no arquivo `src/routes/paths.ts` são referentes aos "caminhos de URL" do frontend
 * (paths), que serão utilizados em outros arquivos para navegação via react-router.
 */

import axios, { AxiosRequestConfig, AxiosInstance, AxiosError } from "axios";

import { API_URL } from "src/config-global";
import {
  STORAGE_KEY_ACCESS,
  STORAGE_KEY_REFRESH,
} from "src/config/storage-keys";

// Variável para controlar se já está renovando o token
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Factory function para criar instâncias axios padronizadas
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      // Se for FormData, não sobrescreva o Content-Type
      // O browser vai setar automaticamente com o boundary correto
      if (!(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json";
      }
      return { ...config };
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const originalRequest: any = error.config;

      // Se for erro 401 (token expirado/inválido) e não for a rota de refresh ou login
      // Também ignora se for uma requisição pública
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/login") &&
        !originalRequest.url?.includes("/auth/refresh") &&
        !originalRequest._isPublicRequest
      ) {
        if (isRefreshing) {
          // Se já está renovando, adiciona na fila
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH);

        if (!refreshToken) {
          // Se não tem refresh token, desloga o usuário
          processQueue(error, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = "/auth/login";

          return Promise.reject(error);
        }

        try {
          // Tenta renovar o token
          const response = await instance.post("/admin/auth/refresh", {
            refreshToken,
          });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          // Salva os novos tokens
          localStorage.setItem(STORAGE_KEY_ACCESS, accessToken);
          if (newRefreshToken) {
            localStorage.setItem(STORAGE_KEY_REFRESH, newRefreshToken);
          }

          // Atualiza o header da instância
          instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Dispara evento customizado para notificar que o token foi atualizado
          window.dispatchEvent(
            new CustomEvent("token-refreshed", { detail: { accessToken } }),
          );

          // Processa a fila de requisições que falharam
          processQueue(null, accessToken);
          isRefreshing = false;

          // Refaz a requisição original
          return await instance(originalRequest);
        } catch (refreshError) {
          // Se falhar ao renovar, desloga o usuário
          processQueue(refreshError, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = "/auth/login";

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(
        (error.response && error.response.data) || "Something went wrong",
      );
    },
  );

  return instance;
};

export type axiosInstanceType = "axiosInstance";

// Criar as instâncias
export const axiosInstance = createAxiosInstance(API_URL);

// Factory function para criar fetchers
const createFetcher =
  (instance: AxiosInstance) =>
  async (args: string | [string, AxiosRequestConfig]) => {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await instance.get(url, { ...config });
    return res.data;
  };

// Criar os fetchers
export const appFetcher = createFetcher(axiosInstance);

// Endpoints da App API
export const endpoints = {
  motor: {
    rules: {
      getAll: "/motor/rules",
      getActive: "/motor/rules/active",
      create: "/motor/rules/create",
      update: "/motor/rules/update",
      inactivate: "/motor/rules/inactivate",
      delete: "/motor/rules/delete",
    },
    conversionRates: {
      getAll: "/motor/conversion-rates",
      getActive: "/motor/conversion-rates/active",
      create: "/motor/conversion-rates/create",
      update: "/motor/conversion-rates/update",
      inactivate: "/motor/conversion-rates/inactivate",
      activate: "/motor/conversion-rates/activate",
      delete: "/motor/conversion-rates/delete",
    },
    variables: "/variables",
    upload: {
      file: "bucket/upload/values",
    },
  },
  admin: {
    adminLogs: "/admin/adminlog",
    adminUser: {
      getAll: "/admin/admin-users",
      getById: "/admin/admin-user",
      create: "/admin/admin-user/create",
      update: "/admin/admin-user/update",
      inactivate: "/admin/admin-user/inactivate",
      delete: "/admin/admin-user/delete",
    },
    auth: {
      register: "/api/auth/register",
      login: "/admin/auth/login",
      refresh: "/admin/auth/refresh", // Refresh access token
    },
    whitelabel: "/admin/whitelabel",
  },
  appUsers: {
    getAll: "/app-users",
    getById: "/app-users",
    update: "/app-users/update",
  },
  terms: {
    getAll: "/terms",
    getLatest: "/terms/latest",
    getById: "/terms", // O ID será adicionado no repositório: /terms/:id
    create: "/terms/create",
    edit: "/terms/edit", // O ID será adicionado no repositório: /terms/edit/:id
    publish: "/terms/publish", // O ID será adicionado no repositório: /terms/publish/:id
    activate: "/terms/activate", // O ID será adicionado no repositório: /terms/activate/:id
    inactivate: "/terms/inactivate", // O ID será adicionado no repositório: /terms/inactivate/:id
  },
  policies: {
    getAll: "/policies",
    getLatest: "/policies/latest",
    getById: "/policies", // O ID será adicionado no repositório: /policies/:id
    create: "/policies/create",
    edit: "/policies/edit", // O ID será adicionado no repositório: /policies/edit/:id
    publish: "/policies/publish", // O ID será adicionado no repositório: /policies/publish/:id
    activate: "/policies/activate", // O ID será adicionado no repositório: /policies/activate/:id
    inactivate: "/policies/inactivate", // O ID será adicionado no repositório: /policies/inactivate/:id
  },
  shoppingSettings: {
    get: "/settings",
    update: "/settings/update",
    uploadLogo: "/settings/upload-logo",
  },
  benefits: {
    getAll: "/benefits/all", // Lista todos os benefícios (Admin/User)
    search: "/benefits/search", // Busca benefícios com termo 'q' e paginação (GET)
    getById: "/benefits", // O ID será adicionado no repositório: /benefits/:id
    uploadImage: "/files/upload", // Upload de imagem para o benefício (POST)
    redeems: {
      getUserRedeems: "/benefits/redeems", // (User) Lista todas as reservas do usuário autenticado
      getAllRedeems: "/benefits/redeems/all", // (Admin) Lista todas as reservas
      getAllWithBenefits: "/benefits/redeems/with-benefits", // (Admin) Lista todas as reservas com detalhes dos benefícios
    },
    products: {
      create: "/products/create", // Cria produto (POST)
      update: "/products/update", // O ID será adicionado no repositório: /products/update/:id (PUT)
      delete: "/products/delete", // O ID será adicionado no repositório: /products/delete/:id (DELETE)
      activate: "/products/activate", // O ID será adicionado no repositório: /products/activate/:id (PATCH)
      inactivate: "/products/inactivate", // O ID será adicionado no repositório: /products/inactivate/:id (PATCH)
    },
  },
  offers: {
    getAll: "/offers/all",
    create: "/offers/create",
    update: "/offers/update", // O ID será adicionado no repositório: /offers/update/:id (PUT)
    delete: "/offers/delete", // O ID será adicionado no repositório: /offers/delete/:id (DELETE)
  },
  sellers: {
    getAll: "/seller/admin",
    getActive: "/seller",
    getById: "/seller",
    create: "/seller",
    update: "/seller",
    delete: "/seller",
  },
};
